import React, { useState, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
  SvgIcon,
  Menu,
  ListItemIcon,
  ListItemText,
  withStyles,
  makeStyles
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import GetAppIcon from '@material-ui/icons/GetApp';
import FilterListIcon from '@material-ui/icons/FilterList';
import { Pagination, Autocomplete } from '@material-ui/lab';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { showGlobalToast } from 'src/features/uiSlice';
import { messageToastType_const } from 'src/app/constant/config';

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#f5f6fa',
    color: '#45485E',
    fontWeight: 700,
    fontSize: 13,
    whiteSpace: 'nowrap',
    borderBottom: '2px solid #e0e3ef'
  },
  body: {
    fontSize: 13,
    color: '#333'
  },
  root: { padding: '10px 16px' }
}))(TableCell);

const useStyles = makeStyles(theme => ({
  summaryCardGreen: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: '16px 22px',
    border: '1px solid #e2e8f0',
    borderLeft: '4px solid #10b981',
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
    transition: 'all 0.2s',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(16,185,129,0.12)'
    }
  },
  summaryCardBlue: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: '16px 22px',
    border: '1px solid #e2e8f0',
    borderLeft: '4px solid #3b82f6',
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
    transition: 'all 0.2s',
    '&:hover': {
      boxShadow: '0 4px 16px rgba(59,130,246,0.12)'
    }
  },
  summaryCardTitle: {
    fontSize: 12,
    color: '#64748b',
    fontWeight: 700,
    letterSpacing: '0.5px',
    marginBottom: 6,
    textTransform: 'uppercase'
  },
  summaryCardValueGreen: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a'
  },
  summaryCardValueBlue: {
    fontSize: 22,
    fontWeight: 700,
    color: '#0f172a'
  },
  iconBadgeGreen: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#ecfdf5',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  iconBadgeBlue: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#eff6ff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  toolbar: {
    padding: 16,
    marginBottom: 16,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    flexWrap: 'wrap',
    backgroundColor: '#fff',
    border: '1px solid #e0e3ef',
    boxShadow: 'none'
  },
  searchInput: {
    flex: '1 1 220px',
    minWidth: 200,
    maxWidth: 340,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    }
  },
  filterSelect: {
    minWidth: 150,
    '& .MuiOutlinedInput-root': {
      borderRadius: 8
    },
    '& .MuiInputLabel-root': {
      fontSize: 13
    },
    '& .MuiOutlinedInput-input': {
      fontSize: 13,
      padding: '10.5px 14px'
    }
  }
}));

const PaymentReceiptsTable = ({
  receipts = [],
  totalReceipts = 0,
  page = 1,
  setPage,
  pageSize = 12,
  totalAmountSum = 0,
  onOpenAddModal,
  onEditReceipt,
  onDeleteReceipt,
  formatMoney = amount => `${amount}`,
  isAdmin,
  isAccountant,
  isSale,
  isLeadMarketing,
  isAgency,
  currentAgencyId,
  search,
  setSearch,
  searchType: externalSearchType,
  setSearchType: setExternalSearchType,
  fromDate,
  setFromDate,
  toDate,
  setToDate,
  accountFilter,
  setAccountFilter,
  agencyFilter,
  setAgencyFilter,
  agencyOrderTotalAmount,
  agencyOrderCount,
  listAgencies = [],
  agencyDebtMap = {},
  listStaff = [],
  listUsers = [],
  listSales = [],
  dataLogin = null
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [internalSearchType, setInternalSearchType] = useState(
    agencyFilter && agencyFilter !== 'all' ? 'AGENCY' : 'ALL'
  );
  const searchType = externalSearchType !== undefined ? externalSearchType : internalSearchType;
  const setSearchType = setExternalSearchType || setInternalSearchType;

  React.useEffect(() => {
    if (agencyFilter && agencyFilter !== 'all') {
      setSearchType('AGENCY');
    }
  }, [agencyFilter, setSearchType]);

  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [selectedReceipt, setSelectedReceipt] = useState(null);

  const handleActionMenuOpen = (event, item) => {
    setActionAnchorEl(event.currentTarget);
    setSelectedReceipt(item);
  };

  const handleActionMenuClose = () => {
    setActionAnchorEl(null);
    setSelectedReceipt(null);
  };

  const handleFilterClick = event => {
    setFilterAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const activeFilterCount = [
    accountFilter && accountFilter !== 'all',
    agencyFilter && agencyFilter !== 'all'
  ].filter(Boolean).length;

  const handleCopyText = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    dispatch(
      showGlobalToast({
        message: `Đã sao chép ${label}: ${text}`,
        type: messageToastType_const.success
      })
    );
  };

  const flatAgencies = useMemo(() => {
    const flatten = (agencies, parentName = '') => {
      const result = [];
      (agencies || []).forEach(agency => {
        if (!agency) return;
        result.push({ ...agency, parentName });
        if (agency.children && agency.children.length > 0) {
          result.push(...flatten(agency.children, agency.name || parentName));
        }
      });
      return result;
    };
    return flatten(listAgencies || []);
  }, [listAgencies]);

  const agencyNames = useMemo(() => {
    return new Set(
      flatAgencies
        .map(a => (a.name || a.fullname || '').trim().toLowerCase())
        .filter(Boolean)
    );
  }, [flatAgencies]);

  const usersLookupMap = useMemo(() => {
    const byId = {};
    const byName = {};
    const byUsername = {};

    const register = (u, defaultRole = '') => {
      if (!u) return;
      const uid = u.id ? String(u.id) : null;
      const fn = (u.full_name || u.name || '').trim().toLowerCase();
      const un = (u.username || '').trim().toLowerCase();

      const userRole = u.role?.title || (typeof u.role === 'string' ? u.role : '') || u.role_name || defaultRole;
      const roleId = Number(u.role_id || u.roleId || u.role?.id || (defaultRole === 'Sale' ? 2 : 0));

      const entry = {
        ...u,
        role_id: roleId,
        role_name: userRole,
        role: u.role || (userRole ? { id: roleId, title: userRole } : undefined)
      };

      if (uid) byId[uid] = entry;
      if (fn) byName[fn] = entry;
      if (un) byUsername[un] = entry;
    };

    // ONLY staff and sales, never customers
    (listSales || []).forEach(u => register(u, 'Sale'));
    (listStaff || []).forEach(u => register(u, ''));
    if (dataLogin) register(dataLogin, '');

    return { byId, byName, byUsername };
  }, [listSales, listStaff, dataLogin]);

  const getRoleDisplayName = (userOrRole, fallback = '') => {
    if (!userOrRole) return fallback || '';

    if (typeof userOrRole === 'string') {
      const raw = userOrRole.trim().toLowerCase();
      if (raw.includes('admin') || raw.includes('quản trị')) return 'Quản trị';
      if (raw.includes('kế toán') || raw.includes('accountant')) return 'Kế toán';
      if (raw.includes('cskh') || raw.includes('customer_care') || raw.includes('chăm sóc')) return 'CSKH';
      if (raw.includes('kỹ thuật') || raw.includes('technician') || raw.includes('tech')) return 'Kỹ thuật';
      if (raw.includes('lead') || raw.includes('marketing')) return 'Lead Marketing';
      if (raw.includes('đại lý') || raw.includes('agency')) return 'Đại lý';
      if (raw.includes('sale') || raw.includes('bán hàng')) return 'Sale';
      if (raw.includes('tài xế') || raw.includes('driver')) return 'Tài xế';
      if (raw.includes('enduser') || raw.includes('customer')) return 'Sale';
      if (raw.includes('hệ thống')) return 'Hệ thống';
      if (userOrRole.trim()) return userOrRole.trim();
    }

    const roleId = Number(
      userOrRole?.role_id ||
      userOrRole?.roleId ||
      userOrRole?.role?.id ||
      (typeof userOrRole === 'number' ? userOrRole : 0)
    );

    const rawRole = String(
      userOrRole?.role?.title ||
      userOrRole?.role ||
      userOrRole?.role_name ||
      userOrRole?.roleName ||
      ''
    ).toLowerCase().trim();

    if (roleId === 1 || rawRole === 'admin' || rawRole.includes('quản trị')) return 'Quản trị';
    if (roleId === 2 || rawRole === 'sale' || rawRole.includes('bán hàng')) return 'Sale';
    if (roleId === 3 || rawRole === 'customer_care' || rawRole.includes('chăm sóc') || rawRole.includes('cskh')) return 'CSKH';
    if (roleId === 4 || rawRole === 'agency' || rawRole.includes('đại lý')) return 'Đại lý';
    if (roleId === 7 || rawRole === 'accountant' || rawRole.includes('kế toán')) return 'Kế toán';
    if (roleId === 8 || rawRole === 'technician' || rawRole.includes('kỹ thuật') || rawRole.includes('tech')) return 'Kỹ thuật';
    if (roleId === 10 || rawRole === 'driver' || rawRole.includes('tài xế')) return 'Tài xế';
    if (roleId === 11 || rawRole === 'lead_marketing' || rawRole.includes('lead') || rawRole.includes('marketing')) return 'Lead Marketing';
    if (roleId === 5 || rawRole.includes('enduser') || rawRole.includes('customer')) return 'Sale';

    if (rawRole.includes('admin') || rawRole.includes('quản trị')) return 'Quản trị';
    if (rawRole.includes('accountant') || rawRole.includes('kế toán')) return 'Kế toán';
    if (rawRole.includes('cskh') || rawRole.includes('chăm sóc')) return 'CSKH';
    if (rawRole.includes('kỹ thuật') || rawRole.includes('tech')) return 'Kỹ thuật';
    if (rawRole.includes('sale')) return 'Sale';
    if (rawRole.includes('marketing')) return 'Lead Marketing';

    if (userOrRole?.role?.title) return userOrRole.role.title;
    if (userOrRole?.role_name && !userOrRole.role_name.toLowerCase().includes('enduser')) return userOrRole.role_name;

    return fallback || (roleId ? `Role ${roleId}` : 'Sale');
  };

  const filteredReceipts = useMemo(() => {
    if (!receipts) return [];
    let list = receipts;
    if (searchType === 'END_USER') {
      list = receipts.filter(r => {
        const pType = r.payer_type || r.customer_type;
        if (pType === 'AGENCY') return false;
        const pName = (r.payer_name || r.customer_name || '').trim().toLowerCase();
        if (pName && agencyNames.has(pName)) return false;
        return true;
      });
    }
    return [...list].sort((a, b) => {
      const timeA = new Date(a.created_at || a.createdAt || a.receipt_date || a.date || 0).getTime();
      const timeB = new Date(b.created_at || b.createdAt || b.receipt_date || b.date || 0).getTime();
      return timeB - timeA;
    });
  }, [receipts, searchType, agencyNames]);

  const uniqueAccounts = useMemo(() => {
    const list = receipts
      .map(r => r.account || r.target_account)
      .filter(acc => acc && typeof acc === 'string' && acc.trim() !== '');
    return Array.from(new Set(list));
  }, [receipts]);

  // Calculations
  const calculatedTotal = useMemo(() => {
    if (searchType === 'END_USER') {
      return filteredReceipts.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
    }
    return totalAmountSum || receipts.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [totalAmountSum, receipts, filteredReceipts, searchType]);

  const todayAmount = useMemo(() => {
    const todayStr = moment().format('YYYY-MM-DD');
    return receipts
      .filter(item => {
        const d = item.receipt_date || item.date;
        return d && moment(d).format('YYYY-MM-DD') === todayStr;
      })
      .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [receipts]);

  const monthAmount = useMemo(() => {
    const monthStr = moment().format('YYYY-MM');
    return receipts
      .filter(item => {
        const d = item.receipt_date || item.date;
        return d && moment(d).format('YYYY-MM') === monthStr;
      })
      .reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  }, [receipts]);

  const handleExportExcel = () => {
    const headers = ['Ngày tháng', 'Mã phiếu', 'Người đưa tiền', 'Số tiền (VNĐ)', 'Tài khoản nhận', 'Ghi chú', 'Người lập'];
    const rows = filteredReceipts.map(r => [
      moment(r.receipt_date || r.date).format('DD/MM/YYYY'),
      r.code || '',
      r.payer_name || r.customer_name || '',
      r.amount || 0,
      r.account || r.target_account || '',
      r.note || '',
      r.creator?.full_name || r.created_by || ''
    ]);

    let csvContent = 'data:text/csv;charset=utf-8,\uFEFF'
      + headers.join(',') + '\n'
      + rows.map(e => e.map(x => `"${String(x).replace(/"/g, '""')}"`).join(',')).join('\n');

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `danh_sach_phieu_thu_${moment().format('YYYYMMDD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box>
      {/* ── Summary Chips ── */}
      <Box display="flex" alignItems="center" style={{ gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        {(() => {
          const selectedAgInfo = agencyFilter && agencyFilter !== 'all' && agencyDebtMap ? (agencyDebtMap[String(agencyFilter)] || null) : null;
          const showAgencySummary = (isAgency || (agencyFilter && agencyFilter !== 'all')) && (selectedAgInfo || agencyOrderTotalAmount !== undefined || isAgency);

          if (!showAgencySummary) {
            return (
              <>
                <Box
                  style={{
                    border: '1px solid #27ae60',
                    borderRadius: 8,
                    padding: '6px 14px',
                    background: '#eafaf1',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography style={{ fontSize: 13, color: '#27ae60', fontWeight: 600 }}>
                    Tổng tiền thu:{' '}
                    <strong style={{ fontSize: 14, color: '#27ae60', fontWeight: 700 }}>
                      {formatMoney(calculatedTotal)}
                    </strong>
                  </Typography>
                </Box>
              </>
            );
          }

          const oCount = selectedAgInfo ? selectedAgInfo.orders.length : (agencyOrderCount || 0);
          const oTotal = selectedAgInfo ? selectedAgInfo.totalSales : (agencyOrderTotalAmount || 0);
          const oPaid = selectedAgInfo ? selectedAgInfo.totalPaid : calculatedTotal;
          const oDebt = selectedAgInfo ? selectedAgInfo.debt : Math.max(0, oTotal - oPaid);

          return (
            <>
              <Box
                style={{
                  border: '1px solid #3b82f6',
                  borderRadius: 8,
                  padding: '6px 14px',
                  background: '#eff6ff',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>
                  Tổng số đơn bán:{' '}
                  <strong style={{ fontSize: 14, color: '#1d4ed8', fontWeight: 700 }}>
                    {oCount} đơn
                  </strong>
                </Typography>
              </Box>

              <Box
                style={{
                  border: '1px solid #6366f1',
                  borderRadius: 8,
                  padding: '6px 14px',
                  background: '#eef2ff',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography style={{ fontSize: 13, color: '#4338ca', fontWeight: 600 }}>
                  Tổng tiền bán:{' '}
                  <strong style={{ fontSize: 14, color: '#4338ca', fontWeight: 700 }}>
                    {formatMoney(oTotal)}
                  </strong>
                </Typography>
              </Box>

              <Box
                style={{
                  border: '1px solid #27ae60',
                  borderRadius: 8,
                  padding: '6px 14px',
                  background: '#eafaf1',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography style={{ fontSize: 13, color: '#27ae60', fontWeight: 600 }}>
                  Đã thu:{' '}
                  <strong style={{ fontSize: 14, color: '#27ae60', fontWeight: 700 }}>
                    {formatMoney(oPaid)}
                  </strong>
                </Typography>
              </Box>

              <Box
                style={{
                  border: '1px solid #e67e22',
                  borderRadius: 8,
                  padding: '6px 14px',
                  background: '#fef9e7',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <Typography style={{ fontSize: 13, color: '#d35400', fontWeight: 600 }}>
                  Còn nợ:{' '}
                  <strong style={{ fontSize: 14, color: '#d35400', fontWeight: 700 }}>
                    {formatMoney(oDebt)}
                  </strong>
                </Typography>
              </Box>
            </>
          );
        })()}
      </Box>

      {/* ── Toolbar & Filters ── */}
      <Card className={classes.toolbar}>
        {/* ── Integrated Search & Category Selector (Đại lý vs Khách lẻ / Khác) ── */}
        {!isAgency ? (
          <Box display="flex" alignItems="center" style={{ flex: '1 1 380px', minWidth: 320, maxWidth: 500 }}>
            <FormControl variant="outlined" size="small" style={{ minWidth: 125 }}>
              <Select
                value={searchType}
                onChange={e => {
                  const val = e.target.value;
                  setSearchType(val);
                  if (val === 'ALL') {
                    setAgencyFilter && setAgencyFilter('all');
                  } else if (val === 'AGENCY') {
                    setSearch && setSearch('');
                  } else if (val === 'END_USER') {
                    setAgencyFilter && setAgencyFilter('all');
                  }
                }}
                style={{
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  backgroundColor: '#f8f9fa',
                  fontSize: 13,
                  fontWeight: 600,
                  color: '#1a1f36'
                }}
              >
                <MenuItem value="ALL" style={{ fontSize: 13, fontWeight: 600 }}>Tất cả</MenuItem>
                <MenuItem value="AGENCY" style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8' }}>Đại lý</MenuItem>
                <MenuItem value="END_USER" style={{ fontSize: 13, fontWeight: 600, color: '#2e7d32' }}>Khách lẻ</MenuItem>
              </Select>
            </FormControl>

            <Box flex={1}>
              {searchType === 'AGENCY' ? (
                <Autocomplete
                  size="small"
                  options={flatAgencies}
                  getOptionLabel={option =>
                    option ? option.name || option.fullname || option.username || `Đại lý #${option.id}` : ''
                  }
                  filterOptions={(options, state) => {
                    const input = (state.inputValue || '').toLowerCase().trim();
                    if (!input) return options;
                    return options.filter(opt => {
                      const name = (opt.name || opt.fullname || opt.username || '').toLowerCase();
                      const code = (opt.code || opt.agency_code || '').toLowerCase();
                      const phone = (opt.leader?.phone || opt.phone || opt.phone_number || '').toLowerCase();
                      const idStr = String(opt.id || '');
                      return name.includes(input) || code.includes(input) || phone.includes(input) || idStr.includes(input);
                    });
                  }}
                  value={
                    flatAgencies.find(a => String(a.id) === String(agencyFilter)) || null
                  }
                  onChange={(event, newValue) => {
                    setAgencyFilter && setAgencyFilter(newValue ? newValue.id : 'all');
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      variant="outlined"
                      placeholder="Chọn hoặc gõ tìm Đại lý..."
                      InputProps={{
                        ...params.InputProps,
                        style: {
                          borderTopLeftRadius: 0,
                          borderBottomLeftRadius: 0,
                          fontSize: 13
                        }
                      }}
                    />
                  )}
                />
              ) : (
                <TextField
                  fullWidth
                  placeholder={
                    searchType === 'END_USER'
                      ? 'Tìm phiếu thu khách lẻ (mã, tên KH, tài khoản)...'
                      : 'Tìm mã phiếu, người nộp, tài khoản...'
                  }
                  variant="outlined"
                  size="small"
                  value={search || ''}
                  onChange={e => setSearch && setSearch(e.target.value)}
                  InputProps={{
                    style: {
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                      fontSize: 13
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon fontSize="small" color="action">
                          <SearchIcon />
                        </SvgIcon>
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearch && setSearch('')}>
                          <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              )}
            </Box>
          </Box>
        ) : (
          <TextField
            placeholder="Tìm mã phiếu, tài khoản, nội dung..."
            variant="outlined"
            size="small"
            value={search || ''}
            onChange={e => setSearch && setSearch(e.target.value)}
            style={{ flex: '1 1 280px', minWidth: 220, maxWidth: 360 }}
            InputProps={{
              style: {
                borderRadius: 8,
                fontSize: 13
              },
              startAdornment: (
                <InputAdornment position="start">
                  <SvgIcon fontSize="small" color="action">
                    <SearchIcon />
                  </SvgIcon>
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch && setSearch('')}>
                    <CloseIcon style={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }}
          />
        )}

        <TextField
          label="Từ ngày"
          type="date"
          placeholder="Từ ngày"
          variant="outlined"
          size="small"
          value={fromDate || ''}
          onChange={e => setFromDate && setFromDate(e.target.value)}
          className={classes.filterSelect}
          InputLabelProps={{
            shrink: true,
            style: {
              backgroundColor: 'white',
              padding: '0 4px',
              marginLeft: -4
            }
          }}
          InputProps={{
            endAdornment: fromDate ? (
              <InputAdornment
                position="end"
                style={{ position: 'absolute', right: 28 }}
              >
                <IconButton
                  size="small"
                  onClick={() => setFromDate && setFromDate('')}
                  style={{ padding: 2 }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />

        <TextField
          label="Đến ngày"
          type="date"
          placeholder="Đến ngày"
          variant="outlined"
          size="small"
          value={toDate || ''}
          onChange={e => setToDate && setToDate(e.target.value)}
          className={classes.filterSelect}
          InputLabelProps={{
            shrink: true,
            style: {
              backgroundColor: 'white',
              padding: '0 4px',
              marginLeft: -4
            }
          }}
          InputProps={{
            endAdornment: toDate ? (
              <InputAdornment
                position="end"
                style={{ position: 'absolute', right: 28 }}
              >
                <IconButton
                  size="small"
                  onClick={() => setToDate && setToDate('')}
                  style={{ padding: 2 }}
                >
                  <CloseIcon style={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null
          }}
        />



        {(search || fromDate || toDate || (accountFilter && accountFilter !== 'all')) && (
          <Tooltip title="Xóa tất cả bộ lọc">
            <IconButton
              size="small"
              onClick={() => {
                setSearch && setSearch('');
                setFromDate && setFromDate('');
                setToDate && setToDate('');
                setAccountFilter && setAccountFilter('all');
              }}
              style={{
                color: '#D9042F',
                border: '1px solid #D9042F33',
                borderRadius: 8,
                padding: 8,
                height: 40,
                width: 40,
                marginTop: -3
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

        <Button
          variant="contained"
          onClick={handleExportExcel}
          startIcon={<GetAppIcon />}
          style={{
            fontWeight: 600,
            height: 40,
            borderRadius: 8,
            backgroundColor: '#10b981',
            color: '#fff',
            marginLeft: 'auto',
            textTransform: 'none',
            boxShadow: '0 2px 8px rgba(16,185,129,0.25)'
          }}
        >
          Xuất Excel
        </Button>

        {(isAdmin || isAccountant) && (
          <Button
            variant="contained"
            color="primary"
            onClick={onOpenAddModal}
            style={{
              fontWeight: 600,
              height: 40,
              borderRadius: 8,
              marginLeft: 12,
              textTransform: 'none'
            }}
          >
            + phiếu thu
          </Button>
        )}
      </Card>

      {/* ── Table View ── */}
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell style={{ width: 110, whiteSpace: 'nowrap' }}>Thời gian</StyledTableCell>
              <StyledTableCell style={{ width: 140, whiteSpace: 'nowrap' }}>Mã phiếu</StyledTableCell>
              <StyledTableCell style={{ minWidth: 200 }}>
                <span style={{ color: '#1976d2' }}>Đại lý</span> / Khách hàng
              </StyledTableCell>
              <StyledTableCell align="right" style={{ width: 130, whiteSpace: 'nowrap' }}>Số tiền</StyledTableCell>
              <StyledTableCell style={{ width: 180 }}>Hình thức thanh toán</StyledTableCell>
              <StyledTableCell style={{ width: 'auto' }}>Ghi chú</StyledTableCell>
              <StyledTableCell style={{ width: 150, whiteSpace: 'nowrap' }}>Người lập</StyledTableCell>
              {(isAdmin || isAccountant) && (
                <StyledTableCell align="center" style={{ width: 90, whiteSpace: 'nowrap' }}>Thao tác</StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredReceipts.length > 0 ? (
              filteredReceipts.map((item, index) => {
                const rDate = item.receipt_date || item.created_at || item.createdAt || item.date;
                const pType = item.payer_type || item.customer_type;
                const pName = item.payer_name || item.customer_name || '--';
                const rCreatorId = item.creator?.id || item.created_by;
                const rawCreatorName = item.creator?.full_name || item.creator?.name || item.creator_name || item.created_by_name || (typeof item.created_by === 'string' ? item.created_by : '') || '';
                const creatorName = (rawCreatorName || 'Hệ thống').split('(')[0].trim();

                let creatorRole = 'Hệ thống';
                if (creatorName && creatorName !== 'Hệ thống') {
                  if (rawCreatorName.includes('(')) {
                    creatorRole = rawCreatorName.split('(')[1].replace(')', '').trim();
                  } else {
                    const matchedReceiptUser =
                      (rCreatorId && usersLookupMap.byId[String(rCreatorId)]) ||
                      (creatorName && usersLookupMap.byName[creatorName.toLowerCase()]) ||
                      (creatorName && usersLookupMap.byUsername[creatorName.toLowerCase()]) ||
                      (dataLogin && (dataLogin.id === rCreatorId || (dataLogin.full_name && dataLogin.full_name.trim().toLowerCase() === creatorName.toLowerCase())) ? dataLogin : null) ||
                      item.creator;

                    creatorRole = getRoleDisplayName(
                      matchedReceiptUser,
                      item.created_by_role || item.creator?.role_name || item.creator?.role?.title || 'Sale'
                    );
                  }
                }
                const isAgencyType = pType === 'AGENCY';

                return (
                  <TableRow key={item.id || index} hover>
                    <StyledTableCell style={{ whiteSpace: 'nowrap' }}>
                      <Box display="flex" flexDirection="column">
                        <Typography style={{ fontWeight: 600, fontSize: 13, color: '#1a1f36' }}>
                          {rDate ? moment(rDate).format('DD/MM/YYYY') : '--'}
                        </Typography>
                        {(() => {
                          const timeSource =
                            (item.created_at && (String(item.created_at).includes('T') || String(item.created_at).includes(':')) ? item.created_at : null) ||
                            (item.createdAt && (String(item.createdAt).includes('T') || String(item.createdAt).includes(':')) ? item.createdAt : null);
                          return timeSource ? (
                            <Typography style={{ fontSize: 12, color: '#888' }}>
                              {moment(timeSource).format('HH:mm')}
                            </Typography>
                          ) : null;
                        })()}
                      </Box>
                    </StyledTableCell>

                    <StyledTableCell style={{ whiteSpace: 'nowrap' }}>
                      <Typography
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: '#1a1f36',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          whiteSpace: 'nowrap'
                        }}
                        onClick={() => handleCopyText(item.code, 'mã phiếu thu')}
                      >
                        <span>{item.code || '--'}</span>
                        <FileCopyOutlinedIcon style={{ fontSize: 12, color: '#888' }} />
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography
                        style={{
                          fontWeight: 600,
                          fontSize: 13,
                          color: isAgencyType ? '#1976d2' : '#1a1f36',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                        onClick={() => handleCopyText(pName, 'tên khách hàng')}
                      >
                        <span>{pName}</span>
                        <FileCopyOutlinedIcon style={{ fontSize: 12, color: '#888' }} />
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell align="right" style={{ whiteSpace: 'nowrap' }}>
                      <Typography style={{ fontWeight: 500, fontSize: 13, color: '#1a1f36' }}>
                        {formatMoney(item.amount || 0)}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell>
                      {acc === 'Tiền mặt' ? (
                        <Chip
                          label="Tiền mặt"
                          size="small"
                          style={{
                            height: 22,
                            fontSize: 11,
                            fontWeight: 600,
                            backgroundColor: '#e8f0fe',
                            color: '#1a73e8'
                          }}
                        />
                      ) : (
                        <Box display="flex" flexDirection="column" alignItems="flex-start" style={{ gap: 2 }}>
                          <Chip
                            label="Chuyển khoản"
                            size="small"
                            style={{
                              height: 20,
                              fontSize: 10,
                              fontWeight: 600,
                              backgroundColor: '#eaf4fb',
                              color: '#1976d2'
                            }}
                          />
                          {acc && (
                            <Typography style={{ fontSize: 12, fontWeight: 600, color: '#333' }}>
                              {acc}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </StyledTableCell>

                    <StyledTableCell>
                      <Typography style={{ fontSize: 12, color: '#555', whiteSpace: 'pre-line' }}>
                        {item.note || '--'}
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell style={{ whiteSpace: 'nowrap' }}>
                      <Typography style={{ fontSize: 13, fontWeight: 600, color: '#1a1f36' }}>
                        {creatorName || 'Hệ thống'}
                      </Typography>
                      {creatorRole && creatorRole !== creatorName && (
                        <Typography variant="body2" color="textSecondary" style={{ fontSize: 12, color: '#888' }}>
                          {creatorRole}
                        </Typography>
                      )}
                    </StyledTableCell>

                    {(isAdmin || isAccountant) && (
                      <StyledTableCell align="center" style={{ whiteSpace: 'nowrap' }}>
                        <IconButton
                          size="small"
                          onClick={e => handleActionMenuOpen(e, item)}
                        >
                          <MoreVertIcon fontSize="small" style={{ color: '#666' }} />
                        </IconButton>
                      </StyledTableCell>
                    )}
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={isAdmin || isAccountant ? 8 : 7} align="center" style={{ padding: 32 }}>
                  <Typography color="textSecondary">
                    {isAgency ? 'Chưa có phiếu thu tiền nào cho đại lý.' : 'Chưa có phiếu thu tiền nào. Bấm nút "+ Lập phiếu thu" để tạo mới!'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* ── Pagination ── */}
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        style={{
          paddingTop: 12,
          marginTop: 12,
          borderTop: '1px dashed #e0e0e0',
          gap: '15px'
        }}
      >
        <Typography style={{ fontSize: '14px', color: '#555' }}>
          Tổng: {searchType === 'END_USER' ? filteredReceipts.length : (totalReceipts || receipts.length || 0)}
        </Typography>
        <Pagination
          count={Math.ceil((searchType === 'END_USER' ? filteredReceipts.length : (totalReceipts || receipts.length || 0)) / (pageSize || 12))}
          color="primary"
          size="small"
          onChange={(_, p) => setPage && setPage(p)}
          page={page || 1}
          siblingCount={1}
          boundaryCount={2}
          showFirstButton
          showLastButton
        />
      </Box>

      {/* ── Action Menu ── */}
      <Menu
        anchorEl={actionAnchorEl}
        keepMounted
        open={Boolean(actionAnchorEl)}
        onClose={handleActionMenuClose}
        PaperProps={{
          style: {
            minWidth: 140,
            borderRadius: 8,
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)'
          }
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedReceipt && onEditReceipt) {
              onEditReceipt(selectedReceipt);
            }
            handleActionMenuClose();
          }}
          style={{ fontSize: 13 }}
        >
          <ListItemIcon style={{ minWidth: 30, color: '#1976d2' }}>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Chỉnh sửa" primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 500 } }} />
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedReceipt && onDeleteReceipt) {
              onDeleteReceipt(selectedReceipt);
            }
            handleActionMenuClose();
          }}
          style={{ fontSize: 13, color: '#d32f2f' }}
        >
          <ListItemIcon style={{ minWidth: 30, color: '#d32f2f' }}>
            <DeleteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Xóa phiếu" primaryTypographyProps={{ style: { fontSize: 13, fontWeight: 500, color: '#d32f2f' } }} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default PaymentReceiptsTable;
